import { IterableMap } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { TrackingPageItemsInFulfillmentBlock } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import type { TrackingPageShipment } from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { memo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { Flex } from "../../flex";
import { ExternalLink } from "../../link";
import { LineItem } from "../../settings-elements/order/order-details";
import { Text } from "../../text";
import {
  TrackingPageContext,
  TrackingPageEnvironment,
} from "../context/tracking-page-context";
import {
  getBadgeStyles,
  getBodyStyles,
  getButtonBorderRadius,
  getButtonHierarchy,
  getButtonStyles,
  getHeaderStyles,
} from "./shared/block-styles";
import { BlockWrapper } from "./shared/block-wrapper";
import {
  getSelectedShipmentWithFallback,
  getShipmentIndex,
} from "./shared/fulfillment-utils";

export const ItemsInFulfillment = memo(function ItemsInFulfillment({
  imageAspectRatio,
  hidePrice,
  hideQuantity,
  itemsInPackageTextColor,
  itemsInPackageBackgroundColor,
  itemsInPackageBorderRadius,
  buttonSettings,
  showReturnsDeeplink,
  returnsDeeplinkTextColor,
  returnsDeeplinkFontSize,
  showClaimsDeeplink,
  claimsDeeplinkTextColor,
  claimsDeeplinkFontSize,
}: TrackingPageItemsInFulfillmentBlock) {
  const { order, environment, pageSettings } =
    useRequiredContext(TrackingPageContext);
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);

  const { fulfillmentIndex: paramsFulfillmentIndex } = useParams<{
    fulfillmentIndex?: string;
  }>();
  const shipmentIndex = getShipmentIndex(paramsFulfillmentIndex, environment);

  const handleNavigate = (i: number) => {
    // Use relative navigation so this works in both the full customer portal
    // (/orders/:id/track/:fulfillmentIndex) and embedded tracking routes
    const target = paramsFulfillmentIndex !== undefined ? `../${i}` : `./${i}`;
    navigate(target, { relative: "path" });
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Reset the card's internal scroll position to the top
    const scrollableParent = contentRef.current?.parentElement;
    if (scrollableParent) {
      scrollableParent.scrollTop = 0;
    }
  };

  const selectedShipment = getSelectedShipmentWithFallback(
    order?.shipments,
    shipmentIndex,
  );

  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);
  const badgeStyles = getBadgeStyles(pageSettings);
  const buttonStyles = getButtonStyles(
    pageSettings?.pageButtonSettings,
    buttonSettings,
  );
  const buttonHierarchy = getButtonHierarchy(buttonSettings);
  const buttonBorderRadius = getButtonBorderRadius(pageSettings);
  const isLoading = !order || !imageAspectRatio;

  // Use configurable items in package styling or fall back to defaults
  const effectiveItemsInPackageBorderRadius =
    itemsInPackageBorderRadius !== undefined
      ? itemsInPackageBorderRadius
      : pageSettings?.blockSettings.borderRadius;
  const itemsInPackageBackgroundStyle = itemsInPackageBackgroundColor
    ? { backgroundColor: itemsInPackageBackgroundColor }
    : {};
  const itemsInPackageTextStyle = itemsInPackageTextColor
    ? { ...headerStyles, color: itemsInPackageTextColor }
    : headerStyles;

  return (
    <BlockWrapper
      environment={environment}
      loading={isLoading}
      pageSettings={pageSettings}
      withBorder
      withScrollbar
    >
      {order && imageAspectRatio && (
        <Flex dir="column" flex={1} ref={contentRef}>
          {selectedShipment !== undefined ? (
            <>
              <Flex dir="column" gap="none" p="xl">
                <Flex
                  dir="column"
                  gap="none"
                  p="xl"
                  style={{
                    ...itemsInPackageBackgroundStyle,
                    ...(effectiveItemsInPackageBorderRadius !== undefined
                      ? {
                          borderRadius: `${effectiveItemsInPackageBorderRadius}px`,
                        }
                      : {}),
                  }}
                >
                  <Flex align="center" justify="space-between">
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      style={itemsInPackageTextStyle}
                    >
                      Items in this package
                    </Text>
                    <Flex align="center" gap="md">
                      {showReturnsDeeplink &&
                        (order.returnDeeplink ||
                          environment === TrackingPageEnvironment.BUILDER) && (
                          <ExternalLink
                            style={{
                              color: returnsDeeplinkTextColor || "inherit",
                              fontSize: returnsDeeplinkFontSize
                                ? `${returnsDeeplinkFontSize}px`
                                : undefined,
                              textDecoration: "underline",
                            }}
                            url={order.returnDeeplink ?? "#"}
                          >
                            Return items in this order
                          </ExternalLink>
                        )}
                      {showClaimsDeeplink &&
                        (order.claimDeeplink ||
                          environment === TrackingPageEnvironment.BUILDER) && (
                          <ExternalLink
                            style={{
                              color: claimsDeeplinkTextColor || "inherit",
                              fontSize: claimsDeeplinkFontSize
                                ? `${claimsDeeplinkFontSize}px`
                                : undefined,
                              textDecoration: "underline",
                            }}
                            url={order.claimDeeplink ?? "#"}
                          >
                            Start a claim
                          </ExternalLink>
                        )}
                    </Flex>
                  </Flex>
                  <Flex dir="column" gap="2xl" mt="md">
                    <IterableMap
                      items={selectedShipment.lineItems}
                      keyFn={(item) => item.id}
                    >
                      {(item, i) => (
                        <LineItem
                          badgeStyles={badgeStyles}
                          bodyStyles={bodyStyles}
                          headerStyles={headerStyles}
                          hidePrice={hidePrice}
                          hideQuantity={hideQuantity}
                          imageAspectRatio={imageAspectRatio}
                          item={item}
                        />
                      )}
                    </IterableMap>
                  </Flex>
                </Flex>
              </Flex>
              <IterableMap items={order.shipments || []} keyFn={(s) => s.id}>
                {(shipment: TrackingPageShipment, i: number) =>
                  shipment.id === selectedShipment.id ? (
                    <div />
                  ) : (
                    <Flex dir="column" gap="none" key={i} p="xl">
                      <Flex dir="column" gap="none" p="xl">
                        <Flex justify="space-between">
                          <Text
                            fontSize="lg"
                            fontWeight="semibold"
                            style={headerStyles}
                          >
                            Items in another package
                          </Text>
                          <RedoButton
                            dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={{
                              ...buttonStyles,
                              ...buttonBorderRadius,
                            }}
                            hierarchy={buttonHierarchy}
                            onClick={() => handleNavigate(i)}
                            size="xs"
                            text="Track Package"
                          />
                        </Flex>
                        <Flex dir="column" gap="2xl" mt="md">
                          <IterableMap
                            items={shipment?.lineItems ?? []}
                            keyFn={(item) => item.id}
                          >
                            {(item, i) => (
                              <LineItem
                                badgeStyles={badgeStyles}
                                bodyStyles={bodyStyles}
                                headerStyles={headerStyles}
                                hidePrice={hidePrice}
                                hideQuantity={hideQuantity}
                                imageAspectRatio={imageAspectRatio}
                                item={item}
                              />
                            )}
                          </IterableMap>
                        </Flex>
                      </Flex>
                    </Flex>
                  )
                }
              </IterableMap>
            </>
          ) : (
            <Flex dir="column" gap="none" p="3xl">
              <Flex align="center" justify="space-between">
                <Text fontSize="lg" fontWeight="semibold" style={headerStyles}>
                  Items in this order
                </Text>
                <Flex align="center" gap="md">
                  {showReturnsDeeplink &&
                    (order.returnDeeplink ||
                      environment === TrackingPageEnvironment.BUILDER) && (
                      <ExternalLink
                        style={{
                          color: returnsDeeplinkTextColor || "inherit",
                          fontSize: returnsDeeplinkFontSize
                            ? `${returnsDeeplinkFontSize}px`
                            : undefined,
                        }}
                        url={order.returnDeeplink ?? "#"}
                      >
                        Return items in this order
                      </ExternalLink>
                    )}
                  {showClaimsDeeplink &&
                    (order.claimDeeplink ||
                      environment === TrackingPageEnvironment.BUILDER) && (
                      <ExternalLink
                        style={{
                          color: claimsDeeplinkTextColor || "inherit",
                          fontSize: claimsDeeplinkFontSize
                            ? `${claimsDeeplinkFontSize}px`
                            : undefined,
                        }}
                        url={order.claimDeeplink ?? "#"}
                      >
                        Start a claim
                      </ExternalLink>
                    )}
                </Flex>
              </Flex>
              <Flex dir="column" gap="2xl" mt="md">
                <IterableMap
                  items={order.lineItems ?? []}
                  keyFn={(item) => item.id}
                >
                  {(item, i) => (
                    <LineItem
                      badgeStyles={badgeStyles}
                      bodyStyles={bodyStyles}
                      headerStyles={headerStyles}
                      hidePrice={hidePrice}
                      hideQuantity={hideQuantity}
                      imageAspectRatio={imageAspectRatio}
                      item={item}
                    />
                  )}
                </IterableMap>
              </Flex>
            </Flex>
          )}
        </Flex>
      )}
    </BlockWrapper>
  );
});
